<template>
  <div class="workflow-payload-editor">
    <div class="d-flex justify-space-between align-center mb-2">
      <label class="v-label">Data Payload</label>
      <v-btn-toggle :model-value="payloadViewMode" mandatory density="compact" variant="outlined"
        @update:model-value="setPayloadViewMode">
        <v-btn value="visual">Visual</v-btn>
        <v-btn value="json">JSON</v-btn>
      </v-btn-toggle>
    </div>

    <v-textarea v-if="payloadViewMode === 'json'" :model-value="localJson" @update:model-value="updateJson"
      auto-grow rows="8" variant="outlined" :error-messages="jsonPayloadError" />

    <v-form v-else ref="visualForm" @submit.prevent>
      <v-sheet class="workflow-payload-editor__visual pa-4" variant="outlined" rounded>
        <WorkflowPayloadNode v-for="(node, index) in payloadTree" :key="node.id" :node="node"
          parent-type="object" :index="index" :depth="0" :validation-issues="validationIssues"
          @update="handleVisualUpdate" @delete="removeRootField(index)" />

        <v-btn prepend-icon="mdi-plus" variant="outlined" density="compact" class="workflow-payload-editor__add-root"
          @click="addRootField">
          Add Field
        </v-btn>

        <div class="workflow-payload-editor__root-note text-caption text-medium-emphasis">
          Top level is a <span class="font-weight-medium">Group {}</span>
        </div>
      </v-sheet>
    </v-form>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import WorkflowPayloadNode from './WorkflowPayloadNode.vue'
import {
  containsNullPayloadValue,
  createPayloadNode,
  jsonObjectToPayloadTree,
  payloadTreeToJson,
  PayloadNode,
  PayloadValidationIssue,
  validatePayloadNodes
} from '@/utils/workflowPayloadTree'

interface VuetifyFormRef {
  validate: () => Promise<{ valid: boolean }>
}

@Options({
  components: { WorkflowPayloadNode },
  props: {
    modelValue: { type: String, required: true }
  },
  emits: ['update:modelValue'],
  watch: {
    modelValue(newValue: string) {
      if (newValue === this.lastEmittedJson) return
      this.syncFromModelValue(newValue)
    }
  }
})
export default class WorkflowPayloadEditor extends Vue {
  modelValue!: string

  payloadViewMode: 'visual' | 'json' = 'visual'
  payloadTree: PayloadNode[] = []
  localJson = '{}'
  jsonPayloadError = ''
  validationIssues: PayloadValidationIssue[] = []
  private lastEmittedJson = ''

  created() {
    this.syncFromModelValue(this.modelValue)
  }

  syncFromModelValue(value: string) {
    this.localJson = value || '{}'
    const parsed = this.parseJsonObject(this.localJson)
    if (!parsed.ok) {
      this.jsonPayloadError = parsed.error
      this.payloadViewMode = 'json'
      return
    }

    if (containsNullPayloadValue(parsed.value)) {
      this.jsonPayloadError = 'Visual editor does not support null values. Edit this payload in JSON.'
      this.payloadViewMode = 'json'
      this.localJson = JSON.stringify(parsed.value, null, 2)
      this.lastEmittedJson = this.localJson
      return
    }

    this.payloadTree = jsonObjectToPayloadTree(parsed.value)
    this.validationIssues = []
    this.jsonPayloadError = ''
    this.localJson = JSON.stringify(parsed.value, null, 2)
    this.lastEmittedJson = this.localJson
  }

  setPayloadViewMode(value: 'visual' | 'json') {
    if (value === 'visual') {
      const parsed = this.parseJsonObject(this.localJson)
      if (!parsed.ok) {
        this.jsonPayloadError = parsed.error
        this.payloadViewMode = 'json'
        return
      }
      if (containsNullPayloadValue(parsed.value)) {
        this.jsonPayloadError = 'Visual editor does not support null values. Edit this payload in JSON.'
        this.payloadViewMode = 'json'
        return
      }
      this.payloadTree = jsonObjectToPayloadTree(parsed.value)
      this.validationIssues = []
      this.localJson = JSON.stringify(parsed.value, null, 2)
    } else {
      const parsed = this.parseJsonObject(this.localJson)
      if (parsed.ok) {
        this.localJson = JSON.stringify(parsed.value, null, 2)
      }
    }
    this.payloadViewMode = value
  }

  updateJson(value: string) {
    this.localJson = value
    const parsed = this.parseJsonObject(value)
    if (!parsed.ok) {
      this.jsonPayloadError = parsed.error
      return
    }

    if (containsNullPayloadValue(parsed.value)) {
      this.jsonPayloadError = 'Visual editor does not support null values. Edit this payload in JSON.'
    } else {
      this.jsonPayloadError = ''
      this.payloadTree = jsonObjectToPayloadTree(parsed.value)
    }
    this.emitValidJson(JSON.stringify(parsed.value, null, 2))
  }

  addRootField() {
    this.payloadTree.push(createPayloadNode('string'))
    this.handleVisualUpdate()
  }

  removeRootField(index: number) {
    this.payloadTree.splice(index, 1)
    this.handleVisualUpdate()
  }

  handleVisualUpdate() {
    this.validationIssues = validatePayloadNodes(this.payloadTree, 'object')
    void this.validateVisualForm()
    if (this.validationIssues.length > 0) return

    try {
      const payloadObject = payloadTreeToJson(this.payloadTree)
      this.emitValidJson(JSON.stringify(payloadObject, null, 2))
    } catch (error) {
      this.validationIssues = [
        {
          nodeId: '',
          field: 'value',
          message: error instanceof Error ? error.message : 'Invalid data payload'
        }
      ]
    }
  }

  async validateVisualForm() {
    await this.$nextTick()
    const form = this.$refs.visualForm as VuetifyFormRef | undefined
    if (form) {
      await form.validate()
    }
  }

  emitValidJson(value: string) {
    this.localJson = value
    this.lastEmittedJson = value
    this.$emit('update:modelValue', value)
  }

  parseJsonObject(value: string): { ok: true; value: Record<string, unknown> } | { ok: false; error: string } {
    try {
      const parsed = JSON.parse(value || '{}')
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        return { ok: false, error: 'Top level must be a group.' }
      }
      return { ok: true, value: parsed as Record<string, unknown> }
    } catch {
      return { ok: false, error: 'Invalid JSON format.' }
    }
  }
}
</script>

<style lang="scss" scoped>
.workflow-payload-editor__visual {
  position: relative;
  min-height: 150px;
  padding-bottom: 56px !important;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 4px;
}

.workflow-payload-editor__add-root {
  margin-left: 30px;
  border-style: dashed;
}

.workflow-payload-editor__root-note {
  position: absolute;
  right: 16px;
  bottom: 12px;
}
</style>
